"use client"

import { useState } from 'react'
import {
  DURATION_BUCKETS,
  SCROLL_BUCKETS,
  type BreakdownRow,
  type HistogramBucket,
} from './types'
import { isPlainLeftClick } from './plainLeftClick'

// The sortable columns. 'percent' reads visitors too — the % column is
// visitors over a per-panel constant denominator, so it orders identically;
// it keeps its own key only so the sort arrow lands on the header you
// clicked. Engagement values may be undefined (row has no duration/scroll
// data) — the comparator always sinks those rows to the bottom, in either
// direction. Exported (with the array form, for URL-param validation) because
// the dashboard owns each panel's sort state so it can mirror it into the URL.
export const SORT_KEYS = [
  'count',
  'visitors',
  'percent',
  'duration',
  'scroll',
] as const
export type SortKey = (typeof SORT_KEYS)[number]
export type SortState = { key: SortKey; dir: 'asc' | 'desc' }

export const DEFAULT_SORT: SortState = { key: 'count', dir: 'desc' }

// Tiny-sample rows produce garbage rankings when sorted by anything other
// than the default count ordering — a page whose single reader stayed 30
// minutes tops every engagement sort. So every NON-default sort (anything
// but count-descending) only ranks rows with at least this many distinct
// visitors; the default view still shows the full tail.
const MIN_VISITORS_TO_RANK = 10

// How many additional rows each "Load more" click reveals.
const LOAD_MORE_CHUNK = 100

const SORT_COLUMN_VALUE: Record<
  SortKey,
  (row: BreakdownRow) => number | undefined
> = {
  count: row => row.count,
  visitors: row => row.visitors,
  percent: row => row.visitors,
  duration: row => row.avgDurationSeconds,
  scroll: row => row.avgScrollPercent,
}

// Column header: a plain click sorts in place; the href is the sorted
// view's URL, so cmd-click opens it in a new tab.
const SortHeader = ({
  columnKey,
  sort,
  onSort,
  href,
  className,
  children,
}: {
  columnKey: SortKey
  sort: SortState
  onSort: (key: SortKey) => void
  href: string
  className: string
  children: React.ReactNode
}) => {
  const isActive = sort.key === columnKey
  return (
    <a
      href={href}
      onClick={e => {
        if (!isPlainLeftClick(e)) return
        e.preventDefault()
        onSort(columnKey)
      }}
      className={`${className} cursor-pointer text-right leading-tight no-underline hover:text-black ${
        isActive ? 'text-black' : ''
      }`}
    >
      {children}
      {isActive && <span className="ml-0.5">{sort.dir === 'desc' ? '▾' : '▴'}</span>}
    </a>
  )
}

// Compact ("narrow"-style) duration from a raw second count: single-letter unit
// suffixes, largest-unit-first. Seconds only appear under a minute ("30s"); once
// it's a minute or more they're dropped and we round to whole minutes ("5m",
// "1h 5m"). Used for the average time-on-site shown in the cell.
const formatDuration = (seconds: number) => {
  const total = Math.round(seconds)
  if (total < 60) return `${total}s`
  const mins = Math.round(total / 60)
  if (mins < 60) return `${mins}m`
  const hours = Math.floor(mins / 60)
  const remMins = mins % 60
  return remMins === 0 ? `${hours}h` : `${hours}h ${remMins}m`
}

// A single proportional bar sitting next to the average value: its width is this
// row's magnitude as a fraction of the column-wide max (the largest average
// among all rows in this panel's column), so the bars let you eyeball how this
// page / referrer / device ranks against its peers on time-on-site or scroll —
// NOT the distribution behind one row (that's the hover histogram). The filled
// track sits over a faint rail so a near-empty bar still reads as "low, out of
// the full width". `fraction` is data-derived, hence the inline width.
const Sparkline = ({ fraction }: { fraction: number }) => {
  const pct = Math.max(0, Math.min(1, fraction)) * 100
  return (
    <span className="relative h-1.5 w-10 bg-[#2A623D]/10" aria-hidden="true">
      <span
        className="absolute inset-y-0 left-0 bg-[#2A623D]/60"
        style={{ width: `${pct}%` }}
      />
    </span>
  )
}

// A mini bar-chart tooltip: one bar per histogram bucket, height proportional
// to that bucket's share of the tallest. Styled to match DailyBarChart's dark
// hover card (same near-black panel, sage bars, tick labels) so the two feel
// like one system. Right-anchored (`right-0`) because it only ever hangs off
// the panel's two rightmost columns, where a left-anchored card would overflow.
const HistogramTooltip = ({
  title,
  summary,
  buckets,
  counts,
  sampleNoun,
}: {
  title: string
  summary: string
  buckets: HistogramBucket[]
  counts: number[]
  // What each counted sample is, for the footer's "% of N <noun> per bucket".
  // Duration is sampled per pageview; scroll per page-exit ($pageleave) — the
  // two histograms count different things, so the noun can't be hardcoded.
  sampleNoun: string
}) => {
  const max = Math.max(1, ...counts)
  const total = counts.reduce((sum, n) => sum + n, 0)
  return (
    <div className="pointer-events-none absolute bottom-full right-0 z-10 mb-1.5 w-max border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-left text-[11px] leading-snug text-white shadow-lg">
      <div className="mb-1 flex items-baseline justify-between gap-4">
        <span className="font-semibold">{title}</span>
        <span className="tabular-nums text-neutral-300">{summary}</span>
      </div>
      <div className="flex h-16 items-end gap-1">
        {counts.map((count, index) => (
          <div key={index} className="flex w-5 flex-col items-center gap-1">
            <span className="text-[9px] tabular-nums text-neutral-400">
              {/* Share of the sample in this bucket — the histogram's y read. */}
              {total > 0 ? Math.round((count / total) * 100) : 0}
            </span>
            <div className="flex h-9 w-full items-end">
              <div
                className="w-full bg-[#2A623D]"
                style={{ height: `${(count / max) * 100}%` }}
              />
            </div>
            <span className="text-[9px] leading-none text-neutral-400">
              {buckets[index]?.label ?? ''}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-1 border-t border-neutral-700 pt-0.5 text-[10px] text-neutral-400">
        % of {total.toLocaleString()} {sampleNoun} per bucket
      </div>
    </div>
  )
}

// One engagement cell: the average value, revealing a histogram tooltip on
// hover. Renders a plain "—" (no tooltip) when this row has no data for the
// metric — e.g. a page with pageviews but no pageleaves to source scroll from.
const EngagementCell = ({
  average,
  fraction,
  histogram,
  title,
  summary,
  buckets,
  sampleNoun,
}: {
  average: string | null
  // This row's average as a share of the column-wide max, drawn as the inline
  // bar. The histogram still feeds only the hover tooltip's distribution.
  fraction: number
  histogram: number[] | undefined
  title: string
  summary: string
  buckets: HistogramBucket[]
  sampleNoun: string
}) => {
  const [hovered, setHovered] = useState(false)
  if (average === null || !histogram) {
    return <span className="relative w-24 text-right tabular-nums text-[#bbb]">—</span>
  }
  return (
    <span
      className="relative flex w-24 cursor-default items-center justify-end gap-1.5 whitespace-nowrap text-right tabular-nums text-[#555]"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="underline decoration-dotted decoration-[#bbb] underline-offset-2">
        {average}
      </span>
      <Sparkline fraction={fraction} />
      {hovered && (
        <HistogramTooltip
          title={title}
          summary={summary}
          buckets={buckets}
          counts={histogram}
          sampleNoun={sampleNoun}
        />
      )}
    </span>
  )
}

// Shared by the top-pages / referrers / devices / hovers / HUD-click panels:
// ranked rows with a proportional accent-tinted bar behind each label. Bar
// widths are data-derived, hence inline style; everything static is Tailwind.
//
// Every numeric column header is click-to-sort (descending first, again to
// flip). Since `rows` holds the period's full result set, a re-sort can pull
// up rows that were outside the default count-ranked top-N; the render then
// shows the top `maxRows` of whatever the current ordering is. Non-default
// sorts additionally drop rows below MIN_VISITORS_TO_RANK distinct visitors
// (the footer says how many), so tiny-sample outliers can't dominate the
// ranking.
//
// The % column is a row's DISTINCT-visitor reach: `row.visitors` (people who
// did this thing) over `totalVisitors` (the denominator the caller passes) —
// i.e. "% of visitors who viewed this page / came from this source / hovered
// this element". It's people-over-people on both sides, so it's comparable
// across panels regardless of what `count` measures (pageviews vs hovers vs
// clicks). Most panels pass all site visitors in range; the hovers panel passes
// visitors *since hover tracking started* so it isn't diluted by visitors who
// predate the feature. Rows deliberately don't sum to 100%: it's a truncated
// top-N, and one person can appear in several rows (multiple pages, devices).
//
// Shimmer standing in for one engagement cell while the engagement tier is
// still in flight, sized to the loaded cell's footprint so rows don't reflow
// when the real values fold in.
const PendingEngagementCell = () => (
  <span
    className="relative flex w-24 items-center justify-end"
    aria-hidden="true"
  >
    <span className="h-3 w-14 animate-pulse rounded-sm bg-neutral-100" />
  </span>
)

// The two engagement columns (avg time on site, avg scroll depth) appear ONLY
// for panels whose rows carry that data — the pageview panels. Each cell shows
// the average plus an inline bar ranking this row's average against the column
// max, and opens a histogram tooltip on hover showing the full distribution
// behind that average — which matters because these means are heavily skewed (a
// handful of long readers drag the average far above the typical bouncing
// visitor). Those columns arrive AFTER the base rows (the engagement tier runs
// the slow queries), so `engagementPending` reserves their layout with shimmer
// cells until the enriched rows land.
export const BreakdownTable = ({
  title,
  countLabel,
  rows,
  totalVisitors,
  sort,
  onSortChange,
  sortHref,
  maxRows = 15,
  emptyMessage = 'No data in this range.',
  engagementPending = false,
  durationSampleNoun = 'pageviews',
}: {
  title: string
  countLabel: string
  rows: BreakdownRow[]
  totalVisitors: number
  // Sort state is owned by the dashboard (controlled), so every panel's
  // ordering can be mirrored into the page URL and restored from it. The
  // click-to-toggle logic stays here; only the storage moved up.
  sort: SortState
  onSortChange: (sort: SortState) => void
  // URL of this panel under a given sort — each header anchor's href.
  sortHref: (sort: SortState) => string
  // How many rows to display initially ("Load more" reveals LOAD_MORE_CHUNK
  // at a time past this). `rows` is the FULL grouped result set for the
  // period (the API no longer truncates to the displayed count), so sorting
  // by any column can surface rows from anywhere in it; only the render is
  // cut to the top of the current ordering.
  maxRows?: number
  emptyMessage?: string
  // True while this panel's engagement columns (time on site, scroll depth)
  // are still loading: renders their headers and per-row shimmer cells so the
  // table doesn't jump when the enriched rows replace the light ones. Panels
  // that never get engagement data (hovers, HUD clicks) leave it false.
  engagementPending?: boolean
  // What the duration histogram's samples are, for its tooltip footer. The
  // pageview panels sample session duration once per pageview; the plan-exits
  // panel samples it once per page-exit.
  durationSampleNoun?: string
}) => {
  // Descending first; clicking the active column again flips it.
  const nextSort = (key: SortKey): SortState =>
    sort.key === key
      ? { key, dir: sort.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: 'desc' }
  const handleSort = (key: SortKey) => onSortChange(nextSort(key))
  const hrefFor = (key: SortKey) => sortHref(nextSort(key))

  // Rows revealed beyond the initial maxRows via "Load more". Deliberately
  // NOT reset on sort or range changes — collapsing the panel back to the top
  // slice every time the user re-sorts would lose their place.
  const [extraRows, setExtraRows] = useState(0)
  const visibleLimit = maxRows + extraRows

  // Any sort other than the initial count-descending ranking drops rows below
  // the visitor threshold (see MIN_VISITORS_TO_RANK); flipping count to
  // ascending counts as non-default too, since it would otherwise lead with
  // the noise.
  const isDefaultSort = sort.key === 'count' && sort.dir === 'desc'
  const rankableRows = isDefaultSort
    ? rows
    : rows.filter(row => row.visitors >= MIN_VISITORS_TO_RANK)
  const hiddenSmallRows = rows.length - rankableRows.length

  const sortValue = SORT_COLUMN_VALUE[sort.key]
  const sortedRows = [...rankableRows].sort((a, b) => {
    const aVal = sortValue(a)
    const bVal = sortValue(b)
    // Rows without the metric sink to the bottom regardless of direction.
    if (aVal === undefined || bVal === undefined) {
      return (aVal === undefined ? 1 : 0) - (bVal === undefined ? 1 : 0)
    }
    return sort.dir === 'desc' ? bVal - aVal : aVal - bVal
  })
  const visibleRows = sortedRows.slice(0, visibleLimit)

  // The count-bar scale is computed over the FULL row set, not just the
  // visible slice or the rankable subset: when a re-sort surfaces low-traffic
  // rows, their count bars should read as genuinely small against the panel's
  // biggest row, not get rescaled to fill the width.
  const max = Math.max(1, ...rows.map(row => row.count))
  const denom = Math.max(1, totalVisitors)
  // Only show the engagement columns when at least one row actually has the
  // data — keeps the hovers / HUD-clicks panels (which never populate it) at
  // their original four-column layout.
  const hasEngagement = rows.some(row => row.durationHistogram !== undefined)
  // Column-wide maxes for the inline engagement bars: each cell's bar is its
  // own average over the largest average in the column, so the bars rank rows
  // against each other on time-on-site / scroll depth. Only rows meeting the
  // visitor threshold set the scale — a single-visitor outlier average would
  // squash every other bar. Sub-threshold rows still visible in the default
  // sort just clamp to a full bar.
  const scaleRows = rows.filter(row => row.visitors >= MIN_VISITORS_TO_RANK)
  const maxDuration = Math.max(
    1,
    ...scaleRows.map(row => row.avgDurationSeconds ?? 0),
  )
  const maxScroll = Math.max(
    1,
    ...scaleRows.map(row => row.avgScrollPercent ?? 0),
  )

  return (
    <section className="border border-neutral-200 bg-white px-3 py-2.5">
      <div className="mb-1.5 flex items-end gap-3 border-b border-neutral-100 px-1.5 pb-1 text-[10px] uppercase leading-tight tracking-wide text-[#999]">
        {/* my-0 overrides tufte.css's global h2 margins (3rem/2.5rem), which
            otherwise balloon the panel header. */}
        <h2 className="my-0 min-w-0 flex-1 truncate self-baseline text-[13px] font-semibold normal-case tracking-normal text-black">
          {title}
        </h2>
        <SortHeader columnKey="count" sort={sort} onSort={handleSort} href={hrefFor('count')} className="w-20">
          {countLabel}
        </SortHeader>
        <SortHeader columnKey="visitors" sort={sort} onSort={handleSort} href={hrefFor('visitors')} className="w-16">
          visitors
        </SortHeader>
        <SortHeader columnKey="percent" sort={sort} onSort={handleSort} href={hrefFor('percent')} className="w-14">
          % of<br />visitors
        </SortHeader>
        {hasEngagement ? (
          <>
            <SortHeader columnKey="duration" sort={sort} onSort={handleSort} href={hrefFor('duration')} className="w-24">
              time on<br />site
            </SortHeader>
            <SortHeader columnKey="scroll" sort={sort} onSort={handleSort} href={hrefFor('scroll')} className="w-24">
              scroll<br />depth
            </SortHeader>
          </>
        ) : engagementPending ? (
          // Header slots for the still-loading engagement columns: plain text,
          // not SortHeaders, since there are no values to sort by yet.
          <>
            <span className="w-24 text-right leading-tight">
              time on<br />site
            </span>
            <span className="w-24 text-right leading-tight">
              scroll<br />depth
            </span>
          </>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-[#666]">{emptyMessage}</p>
      ) : rankableRows.length === 0 ? (
        <p className="text-xs text-[#666]">
          No rows reach {MIN_VISITORS_TO_RANK} visitors, so there&apos;s
          nothing to rank by this column — the default {countLabel} sort shows
          the full tail.
        </p>
      ) : (
        <ul className="flex flex-col">
          {visibleRows.map(row => (
            <li
              key={row.label}
              className="relative flex items-baseline gap-3 px-1.5 py-0.5 text-[12.5px] leading-tight odd:bg-neutral-50/60"
            >
              <span
                className="absolute inset-y-0 left-0 bg-[#2A623D]/10"
                style={{ width: `${(row.count / max) * 100}%` }}
              />
              <span className="relative min-w-0 flex-1 truncate" title={row.label}>
                {row.label}
              </span>
              <span className="relative w-20 text-right tabular-nums">
                {row.count.toLocaleString()}
              </span>
              <span className="relative w-16 text-right tabular-nums text-[#888]">
                {row.visitors.toLocaleString()}
              </span>
              <span className="relative w-14 text-right tabular-nums text-[#2A623D]">
                {((row.visitors / denom) * 100).toFixed(1)}%
              </span>
              {!hasEngagement && engagementPending && (
                <>
                  <PendingEngagementCell />
                  <PendingEngagementCell />
                </>
              )}
              {hasEngagement && (
                <>
                  <EngagementCell
                    average={
                      row.avgDurationSeconds !== undefined
                        ? formatDuration(row.avgDurationSeconds)
                        : null
                    }
                    fraction={(row.avgDurationSeconds ?? 0) / maxDuration}
                    histogram={row.durationHistogram}
                    title="Time on site"
                    summary={
                      row.avgDurationSeconds !== undefined
                        ? `avg ${formatDuration(row.avgDurationSeconds)}`
                        : ''
                    }
                    buckets={DURATION_BUCKETS}
                    sampleNoun={durationSampleNoun}
                  />
                  <EngagementCell
                    average={
                      row.avgScrollPercent !== undefined
                        ? `${Math.round(row.avgScrollPercent)}%`
                        : null
                    }
                    fraction={(row.avgScrollPercent ?? 0) / maxScroll}
                    histogram={row.scrollHistogram}
                    title="Scroll depth"
                    summary={
                      row.avgScrollPercent !== undefined
                        ? `avg ${Math.round(row.avgScrollPercent)}%`
                        : ''
                    }
                    buckets={SCROLL_BUCKETS}
                    sampleNoun="page-exits"
                  />
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {rankableRows.length > 0 &&
        (rankableRows.length > visibleRows.length || hiddenSmallRows > 0) && (
          <p className="mb-0 mt-1 px-1.5 text-[10px] text-[#999]">
            {[
              rankableRows.length > visibleRows.length
                ? `Top ${visibleRows.length} of ${rankableRows.length.toLocaleString()} rows under the current sort`
                : null,
              hiddenSmallRows > 0
                ? `${hiddenSmallRows.toLocaleString()} row${
                    hiddenSmallRows === 1 ? '' : 's'
                  } with under ${MIN_VISITORS_TO_RANK} visitors hidden`
                : null,
            ]
              .filter(Boolean)
              .join('; ')}
            .
            {rankableRows.length > visibleRows.length && (
              <button
                type="button"
                onClick={() => setExtraRows(n => n + LOAD_MORE_CHUNK)}
                className="ml-1.5 font-medium text-[#2A623D] hover:underline"
              >
                Load{' '}
                {Math.min(
                  LOAD_MORE_CHUNK,
                  rankableRows.length - visibleRows.length,
                ).toLocaleString()}{' '}
                more
              </button>
            )}
          </p>
        )}
    </section>
  )
}
